// MDC: MD3 with compressed frames (R_LoadMDC in tr_model.c, decoding as in
// tr_surface.c), loaded into the MD3 structure.

import { BinReader, surfaceName } from './bin';
import { XYZ_SCALE, latLongNormal, readShaders, readSt, readTriangles, type MeshModel, type MeshSurface } from './md3';
import { anglesToAxis } from './skeleton';
import type { Vec3 } from '../../shared/math';

const MDC_TAG_ANGLE_SCALE = 360 / 32700;
const MDC_DIST_SCALE = 0.05;
const MDC_MAX_OFS = 127;

/**
 * MDC: base frames stored like MD3 plus per frame compressed offsets from
 * them. Normals of compressed frames come from the base frame (ET decodes them
 * from its 256 entry normal table instead).
 */
export function parseMdc(data: Uint8Array, name = 'mdc'): MeshModel {
  const r = new BinReader(data);
  r.check('IDPC', [2], name);
  const numFrames = r.i32(76);
  const numTags = r.i32(80);
  const numSurfaces = r.i32(84);
  const ofsTagNames = r.i32(96);
  const ofsTags = r.i32(100);
  const ofsSurfaces = r.i32(104);

  const tags = new Map<string, { origin: Float32Array; axis: Float32Array }>();
  for (let t = 0; t < numTags; t++) {
    const origin = new Float32Array(numFrames * 3);
    const axis = new Float32Array(numFrames * 9);
    for (let f = 0; f < numFrames; f++) {
      const o = ofsTags + (f * numTags + t) * 12;
      const angles: Vec3 = [0, 0, 0];
      for (let k = 0; k < 3; k++) {
        origin[f * 3 + k] = r.i16(o + k * 2) * XYZ_SCALE;
        angles[k] = r.i16(o + 6 + k * 2) * MDC_TAG_ANGLE_SCALE;
      }
      const ax = anglesToAxis(angles);
      for (let k = 0; k < 9; k++) axis[f * 9 + k] = ax[Math.trunc(k / 3)][k % 3];
    }
    tags.set(r.str(ofsTagNames + t * 64).toLowerCase(), { origin, axis });
  }

  const surfaces: MeshSurface[] = [];
  let s = ofsSurfaces;
  for (let i = 0; i < numSurfaces; i++) {
    const numCompFrames = r.i32(s + 72);
    const numVerts = r.i32(s + 84);
    const ofsXyz = s + r.i32(s + 104);
    const ofsComp = s + r.i32(s + 108);
    const ofsBase = s + r.i32(s + 112);
    const ofsCompFrames = s + r.i32(s + 116);
    const xyz = new Float32Array(numFrames * numVerts * 3);
    const normals = new Float32Array(numFrames * numVerts * 3);
    for (let f = 0; f < numFrames; f++) {
      const base = r.i16(ofsBase + f * 2);
      const comp = numCompFrames > 0 ? r.i16(ofsCompFrames + f * 2) : -1;
      for (let v = 0; v < numVerts; v++) {
        const o = ofsXyz + (base * numVerts + v) * 8;
        const k = (f * numVerts + v) * 3;
        xyz[k] = r.i16(o) * XYZ_SCALE;
        xyz[k + 1] = r.i16(o + 2) * XYZ_SCALE;
        xyz[k + 2] = r.i16(o + 4) * XYZ_SCALE;
        latLongNormal(r.i16(o + 6), normals, k);
        if (comp >= 0) {
          const ofs = r.u32(ofsComp + (comp * numVerts + v) * 4);
          for (let a = 0; a < 3; a++) xyz[k + a] += (((ofs >>> (a * 8)) & 255) - MDC_MAX_OFS) * MDC_DIST_SCALE;
        }
      }
    }
    surfaces.push({
      name: surfaceName(r.str(s + 4)),
      shaders: readShaders(r, s + r.i32(s + 96), r.i32(s + 80)),
      numVerts,
      indexes: readTriangles(r, s + r.i32(s + 92), r.i32(s + 88)),
      st: readSt(r, s + r.i32(s + 100), numVerts),
      xyz,
      normals,
    });
    s += r.i32(s + 120);
  }
  return { name: r.str(8), numFrames, surfaces, tags };
}
